/**
 * Turning a page's selection into the export request's id narrowing.
 *
 * The request is parked server-side and the download carries only its id (see
 * `createExportSelection`), so nothing here is bounded by a URL any more and no
 * selection can be refused for its size. What remains is a bandwidth question,
 * and a real one: an S&D user's connection is sometimes ~0.25 Mbps, and a
 * select-all on the staged page is 100K+ UUIDs — several MB to upload, minutes
 * of waiting — while the filters that produced it are a few hundred bytes and
 * mean exactly the same thing.
 *
 * So this picks the smallest of the three equivalent ways to say it:
 *
 * - the whole filtered view    -> neither list (the filters alone say it)
 * - the view minus a few rows  -> `excludeIds`
 * - anything else              -> `includeIds`
 *
 * The S&D pages describe their selection in two different ways, so both are
 * accepted. A page that tracks "select-all mode plus exclusions" already knows
 * the answer and needs no round trip; a page holding only a materialized id set
 * has to compare that set against the view, which is a set comparison and never
 * a count — the selection persists across filter changes, so "selected 5, view
 * has 5" does NOT imply they are the same five.
 */

/**
 * Selections at or below this go straight out as `includeIds`.
 *
 * Purely an optimization threshold — it decides whether comparing against the
 * view is worth a round trip, never whether the export is allowed. Crossing it
 * changes nothing a user can observe, which is the point: the previous version
 * of this module had a cap that *rejected* larger selections, and it silently
 * broke every export of 173-200 rows.
 */
const COMPARE_AGAINST_VIEW_ABOVE = 200;

export interface ExportIdNarrowing {
  includeIds?: string[];
  excludeIds?: string[];
}

export type ExportScope =
  /**
   * Everything the current filters match, minus `excludeIds`. What a page in
   * "select all N matching" mode has.
   */
  | {
      kind: 'view';
      excludeIds?: readonly string[];
      /** Consulted only to see if naming what's left is cheaper. */
      fetchAllFilteredIds?: () => Promise<string[]>;
    }
  /**
   * Exactly these rows. What a page holding a materialized checkbox selection
   * has.
   */
  | {
      kind: 'selection';
      selectedIds: readonly string[];
      /** Consulted only to see if the selection is really the whole view. */
      fetchAllFilteredIds?: () => Promise<string[]>;
    };

function unique(ids: readonly string[]): string[] {
  return [...new Set(ids)];
}

async function narrowFromView(
  excludeIds: readonly string[],
  fetchAllFilteredIds?: () => Promise<string[]>,
): Promise<ExportIdNarrowing> {
  const excluded = unique(excludeIds);
  if (excluded.length === 0) return {};
  if (excluded.length <= COMPARE_AGAINST_VIEW_ABOVE) {
    return { excludeIds: excluded };
  }

  // Deselecting most of a large view: the remainder may be far shorter than the
  // exclusion list. Worth one round trip to send the smaller of the two.
  if (!fetchAllFilteredIds) return { excludeIds: excluded };
  const excludedSet = new Set(excluded);
  const remaining = (await fetchAllFilteredIds()).filter(
    (id) => !excludedSet.has(id),
  );
  return remaining.length < excluded.length
    ? { includeIds: remaining }
    : { excludeIds: excluded };
}

async function narrowFromSelection(
  selectedIds: readonly string[],
  fetchAllFilteredIds?: () => Promise<string[]>,
): Promise<ExportIdNarrowing> {
  const selected = unique(selectedIds);

  // Small enough that naming the rows costs nothing. Preferred even when it
  // happens to equal the whole view: naming them is exact, and immune to the
  // view shifting between this call and the export query.
  if (selected.length <= COMPARE_AGAINST_VIEW_ABOVE) {
    return { includeIds: selected };
  }
  // Nothing to compare against — send the rows themselves. Correct at any size;
  // only the upload is bigger than it might have been.
  if (!fetchAllFilteredIds) return { includeIds: selected };

  const allMatching = new Set(await fetchAllFilteredIds());

  // Anything selected that the filters no longer match can't be expressed as
  // "the view minus something", so an exclusion list would quietly change what
  // gets exported. Name the rows instead.
  if (selected.some((id) => !allMatching.has(id))) {
    return { includeIds: selected };
  }

  const selectedSet = new Set(selected);
  const missing = [...allMatching].filter((id) => !selectedSet.has(id));

  // The selection IS the filtered view: the filters alone describe it, and no
  // id list is needed at all. This is the case worth the round trip — it turns
  // a multi-MB upload into a few hundred bytes.
  if (missing.length === 0) return {};
  return missing.length < selected.length
    ? { excludeIds: missing }
    : { includeIds: selected };
}

/** Resolve `scope` into the id narrowing the export request should carry. */
export async function resolveExportNarrowing(
  scope: ExportScope,
): Promise<ExportIdNarrowing> {
  return scope.kind === 'view'
    ? narrowFromView(scope.excludeIds ?? [], scope.fetchAllFilteredIds)
    : narrowFromSelection(scope.selectedIds, scope.fetchAllFilteredIds);
}
